import { Box } from "@mui/material";
import { isNil } from "ramda";
import { useSelector } from "react-redux";
import React from "react";
import ItemsDetailConfigurationViewSection from "../../../common/item/ItemsDetailConfigurationViewSection";
import ItemDetailViewSection, {
  Item,
  ItemDetail,
} from "../../../common/item/ItemDetailViewSection";
import ItemsDetailViewSection, {
  ItemsDetail,
} from "../../../common/item/ItemsDetailViewSection";
import ScrollSpyTabs from "../../../common/nav/ScrollSpyTabs";
import { Data } from "../../../store/terminal/types";
import { Data as MerchantData } from "../../../store/merchant/types";
import { ApplicationState } from "../../../store";
import { Data as CustomGroupData } from "../../../store/customGroup/types";
import useUserAccess from "../../../effects/useUserAccess";
import { get } from "lodash";
import { faTheRedYeti } from "@fortawesome/free-brands-svg-icons";
import { TryRounded } from "@mui/icons-material";

interface TerminalViewDetailProps {
  terminalData: Data;
  merchantData?: any;
}

const centToDollar = (cents: number) => {
  return (cents / 100).toFixed(2).toString(); // convert cents to dollars and round to 2 decimal places
};

const formatTippingPercentageValue = (tippingMode: string, value: number) => {
  if (value) {
    if (tippingMode === "tip_by_percentage") {
      return value.toString() + "%";
    } else if (tippingMode === "tip_by_value") {
      return "$" + value.toString();
    } else if (value) {
      return value.toString();
    }
  } else {
    return "";
  }
};

const convertTimeTo12Hours = function (timeString: string) {
  return new Date("1970-01-01T" + timeString + "Z").toLocaleTimeString(
    "en-US",
    { timeZone: "UTC", hour12: true, hour: "numeric", minute: "numeric" }
  );
};

const TerminalViewDetail = (props: TerminalViewDetailProps) => {
  const { attributes: at } = props.terminalData;
  const { merchantData } = props;
  const merchants: MerchantData[] =
    useSelector((state: ApplicationState) => state.merchant.data?.data) ?? [];

  const access = useUserAccess();
  const SectionAccess = get(access, "Terminal.SectionAccess", {});
  const TerminalInfoAccess = get(SectionAccess, "TerminalInfo.Access", {
    R: false,
    U: false,
  });
  const ProcessorAccess = get(SectionAccess, "Processor.Access", {
    R: false,
    U: false,
  });
  const HostingAccess = get(SectionAccess, "Hosting.Access", {
    R: false,
    U: false,
  });
  const LocationAccess = get(SectionAccess, "Location.Access", {
    R: false,
    U: false,
  });
  const ConfigurationAccess = get(SectionAccess, "Configuration.Access", {
    R: false,
    U: false,
  });
  const OfflineProcessingAccess = get(
    SectionAccess,
    "OfflineProcessing.Access",
    {
      R: false,
      U: false,
    }
  );
  const ReceiptAccess = get(SectionAccess, "Receipt.Access", {
    R: false,
    U: false,
  });
  const SurchargeTippingAccess = get(SectionAccess, "SurchargeTipping.Access", {
    R: false,
    U: false,
  });
  const TerminalKeyAccess = get(SectionAccess, "TerminalKey.Access", {
    R: false,
    U: false,
  });
  const DiningRewardsClubAccess = get(
    SectionAccess,
    "DiningRewardsClub.Access",
    { R: false, U: false }
  );
  const GiftCardAccess = get(SectionAccess, "GiftCard.Access", {
    R: false,
    U: false,
  });
  const PayAtTableAccess = get(SectionAccess, "PayAtTable.Access", {
    R: false,
    U: false,
  });
  // const FixedPaymentsUIAccess = get(SectionAccess, "FixedPaymentsUI.Access", { R: false, U: false });

  const getMerchantNameByMID = (mid: string) => {
    if (mid && merchants && !isNil(merchants)) {
      const merchant = merchants.find(
        (m) => m.attributes.MID?.toLowerCase() === mid.toLowerCase()
      );
      return merchant ? merchant.attributes.Name : null;
    }
    return null;
  };

  const emvOptions =
    useSelector((state: ApplicationState) => state.emv.data) ?? [];

  const emv = emvOptions.find(
    (e) => e.attributes.ConfigName === at.EMVConfigName
  );

  const closeLoopOptions =
    useSelector((state: ApplicationState) => state.closeloop.data) ?? [];

  const closeLoop = closeLoopOptions.find(
    (e) => e.attributes.ConfigName === at.CloseLoopConfigName
  );

  const tokenWalletOptions =
    useSelector((state: ApplicationState) => state.tokenWallet.data) ?? [];

  const tokenWallet = tokenWalletOptions.find(
    (e) => e.attributes.Name === at.TokenWalletFileConfigName
  );

  const customGroups =
    (useSelector<ApplicationState>(
      (store) => store.customGroup.data?.data
    ) as CustomGroupData[]) || [];
  const receipts = useSelector((state: ApplicationState) => state.receipt.data);
  const merchantReceipts =
    receipts && receipts.data
      ? receipts.data
          .filter((data) => data?.attributes?.For === "Merchant")
          .map((i) => i.attributes.Name)
      : [];

  // TM-1264 hide surcharge section for QT850 terminal
  const isHideSurchargeSection = at.TerminalType === "QT 850";

  const customerReceipts =
    receipts && receipts.data
      ? receipts.data
          .filter((data) => data?.attributes?.For === "Customer")
          .map((i) => i.attributes.Name)
      : [];

  const centToDollar = (cents: number) => {
    return (cents / 100).toFixed(2).toString(); // convert cents to dollars and round to 2 decimal places
  };

  // DATA MAPPING
  const terminalInfo: Item[] = [
    { fieldName: "Terminal type", fieldValue: at.TerminalType },
    { fieldName: "Serial number", fieldValue: at.SerNo },
    { fieldName: "TID", fieldValue: at.TID },
    { fieldName: "POI Device ID", fieldValue: at.POIID },
    { fieldName: "Card Prompts", fieldValue: at.CardPrompts },
    { fieldName: "MCC", fieldValue: at.MCC },
    {
      fieldName: "Merchant Name",
      fieldValue: merchantData
        ? merchantData.attributes.Name
        : (getMerchantNameByMID(at.MID as string) as string),
    },
    { fieldName: "Merchant ID", fieldValue: at.MID },
    {
      fieldName: "EMV",
      fieldValue: emv?.attributes.ConfigName || at.EMVConfigName,
    },
    { fieldName: "CPAT Name", fieldValue: at.CPATName },
    {
      fieldName: "Close Loop Config",
      fieldValue: closeLoop?.attributes.ConfigName || at.CloseLoopConfigName,
    },
    {
      fieldName: "Token Wallet Config",
      fieldValue: tokenWallet?.attributes.Name || at.TokenWalletFileConfigName,
    },
    { fieldName: "Note", fieldValue: at.Note },
    {
      fieldName: "Custom Groups",
      fieldValue: at.groups?.length
        ? customGroups
            .filter((group) => at.groups?.includes(group.groupId))
            .map((g) => g.name)
        : undefined,
    },
    {
      fieldName: "Low Value Transaction Limit",
      fieldValue: at.LowValueTransactionLimit
    },
  ];

  const processorItems =
    (at.Processor?.map((prc) => ({
      sectionName: prc.ProcessorName,
      itemData: [
        { fieldName: "MID", fieldValue: prc.MID },
        { fieldName: "Pin Pad Id", fieldValue: prc.PPID },
        { fieldName: "STAN", fieldValue: prc.STAN },
        { fieldName: "TID", fieldValue: prc.TID },
        { fieldName: "Reversal Pending", fieldValue: prc.ReversalPending },
        { fieldName: "Completion Pending", fieldValue: prc.CompletionPending },
      ],

      additionalData: prc.AdditionalData,
    })) as ItemDetail[]) || [];

  const terminalProcessors: ItemsDetail = {
    sectionName: "Processor",
    itemsData: processorItems,
  };

  const terminalApp: ItemsDetail = {
    sectionName: "Applications",
    itemsData:
      (at.Applications?.map((app) => ({
        sectionName: app.AppName,
        itemData: [
          { fieldName: "Version", fieldValue: app.VersionString },
          { fieldName: "Hardware", fieldValue: app.Variant },
        ],
      })) as ItemDetail[]) || [],
  };

  const terminalHosting: Item[] = [
    { fieldName: "URL", fieldValue: at.URL },
    { fieldName: "URL Backup", fieldValue: at.URLBackup },
    { fieldName: "URL Upgrade", fieldValue: at.URLUpgrade },
    { fieldName: "URL Upgrade Backup", fieldValue: at.URLUpgradeBackup },
    { fieldName: "Firmware Upgrade URL", fieldValue: at.URLUpgradeFirmware },
  ];

  const terminalLocation: Item[] = [
    { fieldName: "Location", fieldValue: at.Location },
    { fieldName: "Address 1", fieldValue: at.Address1 },
    { fieldName: "Address 2", fieldValue: at.Address2 },
    { fieldName: "Timezone", fieldValue: at.TZ },
    { fieldName: "Locale", fieldValue: at.Locale },
    { fieldName: "State", fieldValue: at.AddressState },
  ];

  const terminalReceipt: ItemsDetail = {
    sectionName: "Receipt",
    itemsData: [
      {
        sectionName: "Merchant Receipts",
        itemData: [
          { fieldName: "Print Option", fieldValue: at.MerchantReceiptPrint },
          {
            fieldName: "Receipt Template",
            fieldValue:
              at.EnableHTMLReceipts === "No"
                ? at.ReceiptMerchant
                : (at.HTMLReceipts || []).filter((i: string) =>
                    merchantReceipts.includes(i)
                  ),
          },
        ],
      },
      {
        sectionName: "Customer Receipts",
        itemData: [
          { fieldName: "Print Option", fieldValue: at.CustomerReceiptPrint },
          {
            fieldName: "Receipt Template",
            fieldValue:
              at.EnableHTMLReceipts === "No"
                ? at.Receipt
                : (at.HTMLReceipts || []).filter((i: string) =>
                    customerReceipts.includes(i)
                  ),
          },
        ],
      },
      {
        sectionName: "Print Options",
        itemData: [{ fieldName: "Receipt Print", fieldValue: at.ReceiptPrint }],
      },
      {
        sectionName: "Configuration",
        isRTL: true,
        itemData: [
          {
            fieldName: "Show only HTML receipts",
            fieldValue: at.EnableHTMLReceipts,
          },
          {
            fieldName: "Print transaction listing",
            fieldValue: at.PrintTransactionListing,
          },
        ],
      },
    ],
  };
  const preAuthSection = {
    sectionName: "Pre Auth",
    itemData: [
      {
        fieldName: "Pre Auth",
        fieldValue: at.FeaturePreAuth,
      },
      {
        fieldName: "Pre Auth Variance",
        fieldValue:
          at.PreAuth?.VariancePercentage !== null
            ? `${at.PreAuth?.VariancePercentage}%`
            : undefined,
      },
      {
        fieldName: "Pre Auth Expirydays",
        fieldValue:
          at.PreAuth?.ExpiryDays !== null
            ? `${at.PreAuth?.ExpiryDays}%`
            : undefined,
      },
    ],
  };
  const terminalConfiguration: ItemsDetail = {
    sectionName: "Configuration",
    itemsData: [
      {
        sectionName: "Options",
        isRTL: true,
        itemData: [
          { fieldName: "Standalone", fieldValue: at.Standalone },
          { fieldName: "CloudPOS", fieldValue: at.CloudPOS },
          { fieldName: "POSApp", fieldValue: at.POSApp },
          { fieldName: "Cash Out", fieldValue: at.FeatureCashOut },
          {
            fieldName: "No Cash Out In Debit",
            fieldValue: at.NoCashOutInDebit,
          },
          {
            fieldName: "Purchase with Cash Out",
            fieldValue: at.FeaturePurWithCash,
          },
          { fieldName: "Purchase with Tip", fieldValue: at.FeaturePurWithTip },
          { fieldName: "Refund", fieldValue: at.FeatureRefund },
          { fieldName: "Pre-Auth", fieldValue: at.FeaturePreAuth },
          { fieldName: "Completion", fieldValue: at.FeatureCompletion },
          { fieldName: "MOTO", fieldValue: at.FeatureMOTO },
          {
            fieldName: "Manual Settlement",
            fieldValue: at.FeatureManualSettlement,
          },
          { fieldName: "Show Navigation", fieldValue: at.ShowNavigation },
          {
            fieldName: "Show Last Settlement",
            fieldValue: at.FeatureLastSettle,
          },
          {
            fieldName: "Pre-Settlement",
            fieldValue: at.FeaturePreSettle,
          },
          {
            fieldName: "Welfare Restrictions",
            fieldValue: at.WelfareRestriction,
          },
          {
            fieldName: "Duplicate Transaction Check",
            fieldValue: at.DuplicateTxnCheck,
          },
          {
            fieldName: "Idle On Amount Entry",
            fieldValue: at.IdleOnAmountEntry,
          },
          {
            fieldName: "BP Card Support",
            fieldValue: at.BpCardSupport,
          },
          {
            fieldName: "BP Gift Card Support",
            fieldValue: at.FeatureBPGiftCards,
          },
          {
            fieldName: "Terminal Status Reporting",
            fieldValue: at.TerminalStatusReportingEnabled,
          },
          {
            fieldName: "Terminal GPS",
            fieldValue: at.TerminalGPSEnabled,
          },
          {
            fieldName: "Network Connection Manager",
            fieldValue: at.NetworkConnectionManagerEnabled,
          },
          {
            fieldName: "Qantas Loyalty Enabled",
            fieldValue: at.FeatureQantasLoyaltyEnabled,
          },
          {
            fieldName: "Qantas Loyalty Cash Password Protect",
            fieldValue: at.FeatureQantasLoyaltyCashPasswordProtect,
          },
          {
            fieldName: "Partial Payment",
            fieldValue: at.FeaturePartialPaymentEnabled,
          },
          {
            fieldName: "MentorLite",
            fieldValue: at.FeatureMentorLite,
          },
          {
            fieldName: "Lite Theme Enabled",
            fieldValue: at.FeatureLiteThemeEnabled,
          },
          {
            fieldName: "Crypto.com Enabled",
            fieldValue: at.FeatureCryptoDotComEnabled,
          },
          {
            fieldName: "Crypto.com Refunds From Terminal Allowed",
            fieldValue: at.FeatureCryptoDotComRefundsEnabled,
          },
          {
            fieldName: "Pay at Table Enabled",
            fieldValue: at.PayAtTableEnabled,
          },
          {
            fieldName: "Key Entry Password",
            fieldValue: at.KeyEntryPromptPassword,
          },
          {
            fieldName: "MOTO Password",
            fieldValue: at.MOTOPromptPassword,
          },
          {
            fieldName: "Allow Basket Data to Terminal",
            fieldValue: at.AllowBasketData,
          },
          {
            fieldName: "Taxi Mode Enabled",
            fieldValue: at.FeatureA2BTaxiModeEnabled,
          },
          {
            fieldName: "Fastcard Enabled",
            fieldValue: at.FeatureCabchargeFastCardEnabled,
          },
          {
            fieldName: "Enable Self-Installation",
            fieldValue: at.EnableSelfInstall,
          },
          {
            fieldName: "Stationery Order",
            fieldValue: at.EnableStationeryOrder,
          },
          {
            fieldName: "Feature Pair With POS Enabled",
            fieldValue: at.FeaturePairWithPOSEnabled,
          },
          {
            fieldName: "Fleet Card via Fiserv",
            fieldValue: at.FeatureFleetCardAuthCompletion,
          },
          {
            fieldName: "Show Back Button On Present Card Screen",
            fieldValue: at.ShowBackButtonOnPresentCardScreen,
          },
          {
            fieldName: "Show Cancel Button On Present Card Screen",
            fieldValue: at.ShowCancelButtonOnPresentCardScreen,
          },
          {
            fieldName: "FeatureUpdaterEnable",
            fieldValue: at.FeatureUpdaterEnable,
          },
          {
            fieldName: "Fusion API Logging",
            fieldValue: at.FeatureFusionAPILoggingEnabled,
          },
          {
            fieldName: "Reversal Managed On Host",
            fieldValue: at.ReversalManagedOnHost,
          },
          {
            fieldName: "Enable Split Payments",
            fieldValue: at.EnableSplitPayments,
          },
          {
            fieldName: "Show Status Bar",
            fieldValue: at.ShowStatusBar,
          },
          {
            fieldName: "USB POS",
            fieldValue: at.USBPOS,
          },
          {
            fieldName: "Hashed Hot Card Enabled",
            fieldValue: at.HashedHotCardEnabled,
          },
          {
            fieldName: "Hot Card Delta Download Allowed",
            fieldValue: at.IsHotcardDeltaDownloadAllowed,
          },
          {
            fieldName: "Enable Feature Shell Contactless",
            fieldValue: at.FeatureShellContactless,
          },
          {
            fieldName: "Enable Feature PreAuth And Completion",
            fieldValue: at.FeaturePurchaseWithPreAuthAndCompletion,
          },
        ],
      },
      {
        sectionName: "Payment Methods",
        isRTL: true,
        itemData:
          at.PaymentMethods?.filter((method) => method !== "").map(
            (method) =>
              ({
                fieldName: method,
                fieldValue: method ? "Enable" : "Disabled",
              } as Item)
          ) || [],
      },
      {
        sectionName: "Transit Methods",
        itemData: [
          {
            fieldName: "Transit Mode",
            fieldValue: at.TransitMode,
          },
          {
            fieldName: "Disable Login",
            fieldValue: at.DisableLogin,
          },
          {
            fieldName: "Configure Block List Refresh Intervals (in minutes)",
            fieldValue: at.TransitBlockListRefreshIntervalMs,
          },
        ],
      },
      {
        sectionName: "Qantas",
        itemData: [
          {
            fieldName: "Qantas Loyalty Password",
            fieldValue: at.FeatureQantasLoyaltyCashPassword,
          },
          {
            fieldName: "Qantas Loyalty Logo URL",
            fieldValue: at.QantasLoyaltyLogoUrl,
            isImg: true,
          },
        ],
      },
      {
        sectionName: "Timeouts",
        itemData: [
          {
            fieldName: "CloudPOS timeout (sec)",
            fieldValue: at.CloudPosTimeout,
          },
          {
            fieldName: "Inactivity timeout (sec)",
            fieldValue: at.InactivityTimeout,
          },
          { fieldName: "Result timeout (ms)", fieldValue: at.ResultTimeout },
          { fieldName: "Signature Timeout", fieldValue: at.SignatureTimeOut },
          {
            fieldName: "Fallback retry count",
            fieldValue: at.FallbackRetryCount,
          },
          {
            fieldName: "Terminal Status update interval (sec)",
            fieldValue: at.TerminalStatusReportingInterval,
          },
          {
            fieldName: "Crypto.com Refund Availability Window (seconds)",
            fieldValue: at.WindowForCDCRefundOptionSeconds,
          },
          {
            fieldName: "GPS Location update interval (sec)",
            fieldValue: at.TerminalGPSUpdateIntervalSeconds,
          },
          {
            fieldName: "Screen Dimming Timeout (Ms)",
            fieldValue: at.ScreenDimmingTimeoutMs
            //   ? at.ScreenDimmingTimeoutMs / 60
            //   : undefined,
          },
        ],
      },
      {
        sectionName: "Start up",
        itemData: [{ fieldName: "Startup Mode 1", fieldValue: at.StartupMode }],
      },
      {
        sectionName: "Offline Limits",
        itemData: (at.OfflineLimits || [])
          .map((oc) => {
            const items = [];

            items.push({
              fieldName: "Category",
              fieldValue: oc.Category,
            });
            items.push({
              fieldName: "Product",
              fieldValue: oc.Product,
            });
            items.push({
              fieldName: "Amount",
              fieldValue: oc.Amount,
            });

            return items;
          })
          .flat(),
      },
      {
        sectionName: "Offline Config",
        itemData: [
          {
            fieldName: "Offline Processing",
            fieldValue: at.OfflineConfig?.Enabled,
          },
          {
            fieldName: "Offline First",
            fieldValue: at.OfflineConfig?.OfflineFirst,
          },
          {
            fieldName: "Open Loop Floor Limit",
            fieldValue: at.OfflineConfig?.OpenLoopFloorLimit,
          },
          {
            fieldName: "Close Loop Floor Limit",
            fieldValue: at.OfflineConfig?.CloseLoopFloorLimit,
          },
          { fieldName: "Limit Total", fieldValue: at.OfflineConfig?.LimitTotal },
          { fieldName: "Limit Count", fieldValue: at.OfflineConfig?.LimitCount },
        ],
      },
      {
        sectionName: "Merchant Choice Routing (MCR)",
        itemData: [
          {
            fieldName: "MCR",
            fieldValue: !isNaN(at.MCRMinAmount)
              ? centToDollar(at.MCRMinAmount)
              : undefined,
          },
          {
            fieldName: "MCR Mastercard",
            fieldValue: !isNaN(at.MastercardMCRThreshold)
              ? centToDollar(at.MastercardMCRThreshold)
              : undefined,
          },
          {
            fieldName: "MCR Visa",
            fieldValue: !isNaN(at.VisaMCRThreshold)
              ? centToDollar(at.VisaMCRThreshold)
              : undefined,
          },
        ],
      },
      {
        sectionName: "Others",
        itemData: [
          { fieldName: "Medium Priority", fieldValue: at.MediumPriority },
          { fieldName: "Terminal State", fieldValue: at.State },
          { fieldName: "Temp Password", fieldValue: at.TempPassword },
          { fieldName: "Background Logo", fieldValue: at.IdleBitmap as string },
          {
            fieldName: "Refund Transaction Limit",
            fieldValue: !isNaN(at.FeatureRefundLimit)
              ? centToDollar(at.FeatureRefundLimit)
              : undefined,
          },
          {
            fieldName: "Refund Daily Limit",
            fieldValue: !isNaN(at.FeatureRefundDailyLimit)
              ? centToDollar(at.FeatureRefundDailyLimit)
              : undefined,
          },
          {
            fieldName: "Battery Limit",
            fieldValue: at.BatteryLimit,
          },
          {
            fieldName: "Auto Software/Firmware Update Enabled",
            fieldValue: at.AutoSoftwareUpdateEnabled,
          },
          {
            fieldName: "Auto Update Time From",
            fieldValue: convertTimeTo12Hours(at.AutoUpdateTimeFrom as string),
          },
          {
            fieldName: "Auto Update Time To",
            fieldValue: convertTimeTo12Hours(at.AutoUpdateTimeTo as string),
          },
          {
            fieldName: "Enable Self-Installation",
            fieldValue: at.EnableSelfInstall,
          },
          {
            fieldName: "Pin Retry Attempt Count",
            fieldValue: at.PinRetryAttemptCount,
          },
        ],
      },
    ],
  };
  if (at.TerminalType === "QT 850") {
    terminalConfiguration.itemsData.splice(1, 0, preAuthSection);
  }

  const offlineProcessing: Item[] = [
    {
      fieldName: "Offline processing",
      fieldValue: at.OfflineConfig?.Enabled,
    },
    {
      fieldName: "Offline first",
      fieldValue: at.OfflineConfig?.OfflineFirst,
    },
    {
      fieldName: "Open Loop Floor Limit",
      fieldValue: at.OfflineConfig?.OpenLoopFloorLimit,
    },
    {
      fieldName: "Close Loop Floor Limit",
      fieldValue: at.OfflineConfig?.CloseLoopFloorLimit,
    },
    {
      fieldName: "Limit Total",
      fieldValue: at.OfflineConfig?.LimitTotal,
    },
    {
      fieldName: "Limit Count",
      fieldValue: at.OfflineConfig?.LimitCount,
    },
  ];

  const terminalSurchargeTips: ItemsDetail = {
    sectionName: "Surcharge & Tips",
    itemsData: [
      {
        sectionName: "Surcharge Version",
        itemData: [
          {
            fieldName: "Surcharge Version",
            fieldValue: at.SurchargeVersion,
          },
        ],
      },
      {
        sectionName: "Fixed Surcharge Rate",
        itemData: [
          {
            fieldName: "Percentage",
            fieldValue: at.FixedSurchargePercentage,
          },
          {
            fieldName: "Amount",
            fieldValue: at.FixedSurchargeAmount,
          },
        ],
      },
      {
        sectionName: "Surcharge V1 Toggles",
        itemData: [
          {
            fieldName: "Surcharge",
            fieldValue: at.Surcharge,
          },
          {
            fieldName: "Surcharge on tips",
            fieldValue: at.SurchargeTip,
          },
          {
            fieldName: "GST on surcharge",
            fieldValue: at.SurchargeGST,
          },
        ],
      },
      {
        sectionName: "Surcharge V1 Card Types",
        itemData: (at.SurchargePercentage || [])
          .map((per) => {
            const items = [];

            items.push({ fieldName: "Card Type", fieldValue: per.CardType });
            items.push({
              fieldName: "Surcharge",
              fieldValue: per.Percentage.toString() + "%",
            });
            items.push({
              fieldName: "GST on Surcharge",
              fieldValue: per.SurchargeGST,
            });
            return items;
          })
          .flat(),
      },
      {
        sectionName: "Surcharge V2 Toggles",
        itemData: [
          {
            fieldName: "Enable surcharge",
            fieldValue: at.EnableSurchargeV2,
          },
          {
            fieldName: "Surcharge on tips",
            fieldValue: at.SurchargeTip,
          },
          {
            fieldName: "GST on surcharge",
            fieldValue: at.SurchargeGST,
          },
          {
            fieldName: "Enable surcharge settings on terminal",
            fieldValue: at.EnableSurchargeSettingsOnTerminal,
          },
        ],
      },
      {
        sectionName: "Surcharge V2 Card Types",
        itemData: (at.SurchargeV2 || [])
          .map((sur) => {
            const items = [];

            items.push({ fieldName: "Card Type", fieldValue: sur.CardType });
            items.push({
              fieldName: "Surcharge Type",
              fieldValue: sur.SurchargeType,
            });
            items.push({
              fieldName: "Purchase Surcharge",
              fieldValue: sur.PurchaseSurchargeValue,
            });
            items.push({
              fieldName: "Cash Out Surcharge",
              fieldValue: sur.CashoutSurchargeValue,
            });
            items.push({
              fieldName: "GST",
              fieldValue: sur.GSTValue,
            });
            return items;
          })
          .flat(),
      },
      {
        sectionName: "Tipping V1",
        itemData: [
          { fieldName: "Tipping Mode", fieldValue: at.TippingMode },
          {
            fieldName: "Value 1",
            fieldValue: formatTippingPercentageValue(
              at.TippingMode as string,
              at.TipWithPercentagePercentage1 as number
            ),
          },
          {
            fieldName: "Value 2",
            fieldValue: formatTippingPercentageValue(
              at.TippingMode as string,
              at.TipWithPercentagePercentage2 as number
            ),
          },
          {
            fieldName: "Value 3",
            fieldValue: formatTippingPercentageValue(
              at.TippingMode as string,
              at.TipWithPercentagePercentage3 as number
            ),
          },
        ],
      },
      {
        sectionName: "Tipping V2",
        itemData: [
          { fieldName: "Tipping Mode", fieldValue: at.TippingV2?.TippingMode },
          {
            fieldName: "Value 1",
            fieldValue: formatTippingPercentageValue(
              at.TippingV2?.TippingMode as string,
              at.TippingV2?.TipWithPercentage1 as number
            ),
          },
          {
            fieldName: "Value 2",
            fieldValue: formatTippingPercentageValue(
              at.TippingV2?.TippingMode as string,
              at.TippingV2?.TipWithPercentage2 as number
            ),
          },
          {
            fieldName: "Value 3",
            fieldValue: formatTippingPercentageValue(
              at.TippingV2?.TippingMode as string,
              at.TippingV2?.TipWithPercentage3 as number
            ),
          },
        ],
      },
    ],
  };

  const terminalKeys: Item[] = [
    {
      fieldName: "Terminal Master Key",
      fieldValue: !isNil(at.TMK1 || at.TMK2),
    },
    {
      fieldName: "Session Keys",
      fieldValue: isNil(at.TPK || at.TAKs || at.TAKr || at.TEKs || at.TEKr),
    },
    { fieldName: "Manufacturer", fieldValue: at.Manufacturer },
    { fieldName: "Model", fieldValue: at.Model },
    { fieldName: "Model Extended", fieldValue: at.ModelExtended },
    { fieldName: "OS", fieldValue: at.OS },
    { fieldName: "Crypto Date", fieldValue: at.CryptoID },
    { fieldName: "Pin Pad ID", fieldValue: at.PPID },
    { fieldName: "AXISPayVersion", fieldValue: at.AXISPayVersion },
    { fieldName: "STAN", fieldValue: at.STAN },
    { fieldName: "Temp Password", fieldValue: at.TempPassword },
  ];

  const terminalGiftCard: Item[] = [
    {
      fieldName: "Username",
      fieldValue: at.BPGiftCardsConfig?.Username,
    },
    {
      fieldName: "Password",
      fieldValue: at.BPGiftCardsConfig?.Password,
    },
    {
      fieldName: "Legacy Password",
      fieldValue: at.BPGiftCardsConfig?.LegacyPassword,
    },
    {
      fieldName: "Legacy Username",
      fieldValue: at.BPGiftCardsConfig?.LegacyUsername,
    },
    {
      fieldName: "Shop ID",
      fieldValue: at.BPGiftCardsConfig?.ShopId,
    },
    {
      fieldName: "Till ID",
      fieldValue: at.BPGiftCardsConfig?.TillId,
    },
    {
      fieldName: "Terminal ID",
      fieldValue: at.BPGiftCardsConfig?.TerminalId,
    },
    {
      fieldName: "Legacy Terminal ID",
      fieldValue: at.BPGiftCardsConfig?.LegacyTerminalId,
    },
    {
      fieldName: "Currency",
      fieldValue: at.BPGiftCardsConfig?.Currency,
    },
  ];

  const terminalPayAtTable: Item[] = [
    {
      fieldName: "Host Mode",
      fieldValue: at.PayAtTable?.HostMode,
    },
    {
      fieldName: "Host URL",
      fieldValue: at.PayAtTable?.HostURL as string,
    },
    {
      fieldName: "Merchant Password",
      fieldValue: at.PayAtTable?.MerchantPassword as string,
    },
    {
      fieldName: "Store Number",
      fieldValue: at.PayAtTable?.StoreNumber as string,
    },
    {
      fieldName: "User Number",
      fieldValue: at.PayAtTable?.UserNumber as string,
    },
    { fieldName: "Client ID", fieldValue: at.PayAtTable?.ClientId as string },
    {
      fieldName: "Client Secret",
      fieldValue: at.PayAtTable?.ClientSecret as string,
    },
    {
      fieldName: "Token Host Endpoint",
      fieldValue: at.PayAtTable?.TokenHostEndpoint as string,
    },
    {
      fieldName: "Token Host Audience",
      fieldValue: at.PayAtTable?.TokenHostAudience as string,
    },
    {
      fieldName: "Print Merchant Receipt",
      fieldValue: at.PayAtTable?.PrintMerchantReceipt,
    },
    {
      fieldName: "Enable Split Payment",
      fieldValue: at.PayAtTable?.EnableSplitPayment,
    },
  ];

  return (
    <Box>
      <ScrollSpyTabs
        tabsInScroll={[
          {
            label: "Terminal Info",
            component: (
              <ItemDetailViewSection
                sectionName="Terminal Info"
                itemData={terminalInfo}
                isRestricted={!TerminalInfoAccess.R}
              />
            ),
          },
          {
            label: "Processors",
            component: (
              <ItemsDetailViewSection
                sectionName={terminalProcessors.sectionName}
                itemsData={terminalProcessors.itemsData}
                isRestricted={!ProcessorAccess.R}
              />
            ),
          },
          {
            label: "Hosting",
            component: (
              <ItemDetailViewSection
                sectionName="Hosting"
                itemData={terminalHosting}
                isRestricted={!HostingAccess.R}
              />
            ),
          },
          {
            label: "Applications",
            component: (
              <ItemsDetailViewSection
                sectionName={terminalApp.sectionName}
                itemsData={terminalApp.itemsData}
                isRestricted={!HostingAccess.R}
              />
            ),
          },
          {
            label: "Location",
            component: (
              <ItemDetailViewSection
                sectionName="Location"
                itemData={terminalLocation}
                isRestricted={!LocationAccess.R}
              />
            ),
          },
          {
            label: "Receipt",
            component: (
              <ItemsDetailViewSection
                sectionName={terminalReceipt.sectionName}
                itemsData={terminalReceipt.itemsData}
                isColumn={true}
                isRestricted={!ReceiptAccess.R}
              />
            ),
          },
          {
            //create tooltip object and insert it into ItemsDetailViewSection as terminalConfiguration.tooltipTitle?
            label: "Configuration",
            component: (
              <ItemsDetailConfigurationViewSection
                sectionName={terminalConfiguration.sectionName}
                itemsData={terminalConfiguration.itemsData}
                isColumn={true}
                isRestricted={!ConfigurationAccess.R}
              />
            ),
          },
          {
            label: "Offline Processing",
            component: (
              <ItemDetailViewSection
                sectionName="Offline Processing"
                itemData={offlineProcessing}
                isRestricted={!OfflineProcessingAccess.R}
              />
            ),
          },
          ...(!isHideSurchargeSection
            ? [
                {
                  label: "Surcharge & Tips",
                  component: (
                    <ItemsDetailViewSection
                      sectionName={terminalSurchargeTips.sectionName}
                      col={2}
                      itemsData={terminalSurchargeTips.itemsData}
                      isSurcharge={true}
                      isRestricted={!SurchargeTippingAccess.R}
                    />
                  ),
                },
              ]
            : []),
          ...(at.FeatureBPGiftCards === "Yes"
            ? [
                {
                  label: "Gift Card",
                  component: (
                    <ItemDetailViewSection
                      sectionName="Gift Card"
                      itemData={terminalGiftCard}
                      isRestricted={!GiftCardAccess.R}
                    />
                  ),
                },
              ]
            : []),
          ...(at.PayAtTableEnabled === "Yes"
            ? [
                {
                  label: "PayAtTable",
                  component: (
                    <ItemDetailViewSection
                      sectionName="PayAtTable"
                      itemData={terminalPayAtTable}
                      isRestricted={!PayAtTableAccess.R}
                    />
                  ),
                },
              ]
            : []),
          ...(at.FeatureDRCLoyaltyEnabled === "Yes"
            ? [
                {
                  label: "Dining Rewards Club",
                  component: (
                    <ItemDetailViewSection
                      sectionName="Dining Rewards Club"
                      itemData={[
                        {
                          fieldName: "Minimum Loyalty Amount",
                          fieldValue: at.DRCMinLoyaltyAmount,
                        },
                        { fieldName: "Store ID", fieldValue: at.DRCStoreId },
                        {
                          fieldName: "Loyalty Logo Url",
                          fieldValue: at.DRCLoyaltyLogoUrl,
                        },
                        {
                          fieldName: "Loyalty Cash Password",
                          fieldValue: at.FeatureDRCLoyaltyCashPassword,
                        },
                        {
                          fieldName: "DRC Cash Password Protect",
                          fieldValue: at.FeatureDRCLoyaltyCashPasswordProtect,
                        },
                      ]}
                      isRestricted={!DiningRewardsClubAccess.R}
                    />
                  ),
                },
              ]
            : []),
          {
            label: "Terminal Key",
            component: (
              <ItemDetailViewSection
                sectionName="Terminal Key"
                itemData={terminalKeys}
                lastItem
                isRestricted={!TerminalKeyAccess.R}
              />
            ),
          },
        ]}
      />
    </Box>
  );
};

export default TerminalViewDetail;
